import { ApiTabResponse, ApiProject, ApiReportResponse } from "@incendium/api";
import CreateNameDialog from "Components/CreateNameDialog/CreateNameDialog";
import produce from "immer";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import { ArrowDropDownCircleOutlined, Delete, Edit } from "@mui/icons-material";
import { saveReportTab } from "features/reports";
import { motion, type Variants } from "motion/react";

const easeOut = [0.25, 0.1, 0.25, 1] as const;

const tabButtonVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.32,
      ease: easeOut,
      delay: 0.12 + i * 0.045,
    },
  }),
};

interface IReportTabTitleProps {
  index: number;
  project: ApiProject;
  report: ApiReportResponse;
  reportTab: ApiTabResponse;
  tabs: ApiTabResponse[];
  setTabs: React.Dispatch<React.SetStateAction<ApiTabResponse[]>>;
  editMode: boolean;
  onRemoveTab: (t: ApiTabResponse) => void;
  onSelect: () => void;
}

function ReportTabTitle({
  index,
  project,
  report,
  reportTab,
  tabs,
  setTabs,
  editMode,
  onRemoveTab,
  onSelect,
}: IReportTabTitleProps) {
  const [openTabNameDialog, setOpenTabNameDialog] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const onEditTabName = async (name: string) => {
    const res = await saveReportTab(
      project?.id as number,
      report.id as number,
      {
        name,
        order: reportTab.order,
      },
      reportTab.id as number
    );

    setTabs(
      produce(tabs, (draft) => {
        const idx = draft.findIndex((t) => t.id === reportTab.id);
        if (idx >= 0) {
          draft[idx] = res;
        }
      })
    );

    setOpenTabNameDialog(false);
  };

  const handleMenuToggle = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onSelect();
    setMenuOpen((prev) => !prev);
  };

  return (
    <ToggleButton
      value={index}
      component={motion.button}
      custom={index}
      variants={tabButtonVariants}
      initial="hidden"
      animate="show"
      whileTap={{
        scale: 0.96,
        transition: {
          type: "spring",
          stiffness: 420,
          damping: 28,
        },
      }}
      sx={{
        minWidth: 100,
        flexShrink: 0,
        whiteSpace: "nowrap",
        gap: 0.5,
      }}
    >
      <Tooltip title={reportTab.name || ""} placement="top">
        <span>{reportTab.name}</span>
      </Tooltip>
      {editMode && (
        <>
          <IconButton
            ref={anchorRef}
            size="small"
            color="inherit"
            onClick={handleMenuToggle}
            sx={{ p: 0.25, ml: 0.25 }}
          >
            <ArrowDropDownCircleOutlined
              fontSize="small"
              sx={{
                transition: "all 0.1s linear",
                transform: menuOpen ? "rotate(180deg)" : undefined,
              }}
            />
          </IconButton>
          {createPortal(
            <>
              <Menu
                anchorEl={anchorRef.current}
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    setOpenTabNameDialog(true);
                  }}
                >
                  <ListItemIcon>
                    <Edit fontSize="small" />
                  </ListItemIcon>
                  Edit Tab Name
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    onRemoveTab(reportTab);
                  }}
                >
                  <ListItemIcon>
                    <Delete fontSize="small" />
                  </ListItemIcon>
                  Delete Tab
                </MenuItem>
              </Menu>
              <CreateNameDialog
                open={openTabNameDialog}
                setOpen={setOpenTabNameDialog}
                title="Edit Tab Name"
                onSaved={onEditTabName}
                name={reportTab.name}
              />
            </>,
            document.body
          )}
        </>
      )}
    </ToggleButton>
  );
}

export default ReportTabTitle;
